import {
  Assignment,
  BarChart,
  DescriptionOutlined,
  EditCalendarOutlined,
  MedicalInformation,
  Timeline,
} from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import {
  alpha,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { GlobalDataContext } from "../../../../../../contexts/GlobalData";
import { VIEW_STATIONARY } from "../../../../../../enums/PermissionTitles";
import { PET_VIEW_TAB_PARAM } from "../../../../../../enums/UrlPaths";
import useUser from "../../../../../../hooks/useUser";
import PetBiometrics from "../../../../../biometrics/components/PetBiometrics";
import PetDocumentsTab from "../../../../../pets/components/PetDocumentsTab";
import PetsAppointments from "../../../../../pets/components/PetsAppointments";
import {
  PET_TAB_APPOINTMENTS,
  PET_TAB_BIOMETRICS,
  PET_TAB_CHARTS,
  PET_TAB_DOCUMENTS,
  PET_TAB_GRAPHS,
  PET_TAB_NOTES,
  PET_TAB_OVERVIEW,
  PET_TAB_SETTINGS,
  PET_TAB_THERAPIES,
} from "../../../../../pets/enums/PetViewTabs";
import PetManagement from "../../../../../pets/singleView/PetManagment";
import PetNotes from "../../../../../pets/singleView/PetNotes";
import TabPanel from "../../../../components/core/TabPanel";
import HealthRecords from "./HealthRecords";
import MedicalChart from "./MedicalChart";
import MedicalGraphs from "./MedicalGraphs";
import MedicalOverview from "./MedicalOverview";
import PetTabSection from "./PetTabSection";

const isClinicOrMaster = (roles) => roles.master || roles.adminOrEmployee;
// The medical summary and therapy history endpoints both require VIEW_STATIONARY,
// which basic tier clinics (and extended tier employees by default) don't have
const canViewStationary = (roles, pet, isPermissionAllowed) =>
  isClinicOrMaster(roles) && isPermissionAllowed(VIEW_STATIONARY);

// Every tab of the single pet view, in display order. The first visible, enabled tab is the default.
const PET_VIEW_TABS = [
  {
    key: PET_TAB_OVERVIEW,
    label: "TabOverview",
    icon: <MedicalInformation />,
    Component: MedicalOverview,
    visible: canViewStationary,
  },
  {
    key: PET_TAB_THERAPIES,
    label: "TabRecords",
    icon: <Assignment />,
    Component: HealthRecords,
    visible: canViewStationary,
  },
  {
    key: PET_TAB_APPOINTMENTS,
    section: true,  
    label: "Appointments",
    icon: <EditCalendarOutlined />,
    Component: PetsAppointments,
    visible: () => true,
  },
  {
    key: PET_TAB_DOCUMENTS,
    section: true,  
    label: "Documents",
    icon: <DescriptionOutlined />,
    Component: PetDocumentsTab,
    visible: () => true,
    testId: "pet.view.tab.files",
  },
  {
    key: PET_TAB_BIOMETRICS,
    section: true,  
    label: "Biometrics",
    icon: <ThermostatIcon />,
    Component: PetBiometrics,
    visible: () => true,
  },
  {
    key: PET_TAB_NOTES,
    section: true,  
    label: "Notes",
    icon: <SpeakerNotesIcon />,
    Component: PetNotes,
    visible: () => true,
  },
  {
    key: PET_TAB_SETTINGS,
    section: true,  
    label: "Settings",
    icon: <SettingsIcon />,
    Component: PetManagement,
    visible: (roles, pet) => Boolean(pet?.active),
    testId: "pet.view.tab.management",
  },
  {
    key: PET_TAB_GRAPHS,
    label: "TabGraphs",
    icon: <Timeline />,
    Component: MedicalGraphs,
    visible: isClinicOrMaster,
    disabled: true, // DISABLED FOR NOW
  },
  {
    key: PET_TAB_CHARTS,
    label: "TabCharts",
    icon: <BarChart />,
    Component: MedicalChart,
    visible: isClinicOrMaster,
    disabled: true, // DISABLED FOR NOW
  },
];

export default function PetViewTabs({ pet }) {
  const { t } = useTranslation();
  const { roles, isLoading } = useUser();
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, setSearchParams] = useSearchParams();

  // The role decides which tabs exist, so nothing is rendered until it's known
  if (isLoading) return <LinearProgress />;

  const tabs = PET_VIEW_TABS.filter((tab) =>
    tab.visible(roles, pet, isPermissionAllowed),
  );
  const selectable = tabs.filter((tab) => !tab.disabled);
  const requested = searchParams.get(PET_VIEW_TAB_PARAM);
  const activeTab = selectable.some((tab) => tab.key === requested)
    ? requested
    : selectable[0]?.key;

  const handleTabChange = (event, newValue) => {
    setSearchParams(
      (params) => {
        params.set(PET_VIEW_TAB_PARAM, newValue);
        return params;
      },
      { replace: true },
    );
  };

  return (
    <>
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              maxWidth: "100%",
              "& .MuiTabs-flexContainer": {
                flexWrap: "nowrap",
              },
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "1rem" },
                py: { xs: 1, sm: 2 },
                textTransform: "none",
                minWidth: { xs: 80, sm: 140 },
                flexDirection: isMobile ? "column" : "row",
                "& svg": {
                  fontSize: { xs: 18, sm: 22 },
                  mb: isMobile ? 0.5 : 0,
                },
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                iconPosition="start"
                icon={tab.icon}
                label={t(tab.label)}
                disabled={tab.disabled}
                data-testid={tab.testId ?? `pet.view.tab.${tab.key}`}
                sx={tab.disabled ? { textDecoration: "line-through" } : null}
              />
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <Box sx={{ overflowX: "hidden", width: "100%" }}>
        {tabs.map(({ key, label, icon, section, Component }) => (
          <TabPanel key={key} value={activeTab} index={key}>
            {section ? (
              <PetTabSection icon={icon} title={t(label)}>
                <Component />
              </PetTabSection>
            ) : (
              <Component />
            )}
          </TabPanel>
        ))}
      </Box>
    </>
  );
}
